import axios from 'axios';
import { ScheduledPayment } from '../types/models';
import { API_BASE } from './config';

type ApiErrorPayload = {
  errors?: string[];
  error?: string;
  message?: string;
};

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorPayload | undefined;
    const validationErrors = data?.errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) return validationErrors.join('\n');
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export async function getScheduledPayments(
  params?: string | { [key: string]: string | number | undefined }
): Promise<ScheduledPayment[]> {
  const url = new URL(`${API_BASE}/scheduled_payments.json`);
  if (typeof params === 'string' && params) {
    url.searchParams.set('month', params);
  } else if (typeof params === 'object' && params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) url.searchParams.set(k, String(v));
    });
  }
  const res = await axios.get<ScheduledPayment[]>(url.toString());
  return res.data;
}

export async function getScheduledPayment(id: number): Promise<ScheduledPayment> {
  const res = await axios.get<ScheduledPayment>(`${API_BASE}/scheduled_payments/${id}.json`);
  return res.data;
}

export async function createScheduledPayment(data: Partial<ScheduledPayment>): Promise<ScheduledPayment> {
  try {
    const res = await axios.post<ScheduledPayment>(`${API_BASE}/scheduled_payments.json`, { scheduled_payment: data });
    return res.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to create scheduled payment'));
  }
}

export async function updateScheduledPayment(id: number, data: Partial<ScheduledPayment>): Promise<ScheduledPayment> {
  try {
    const res = await axios.put<ScheduledPayment>(`${API_BASE}/scheduled_payments/${id}.json`, { scheduled_payment: data });
    return res.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Failed to update scheduled payment'));
  }
}

export async function deleteScheduledPayment(id: number): Promise<void> {
  await axios.delete(`${API_BASE}/scheduled_payments/${id}.json`);
}

export async function retryScheduledPayment(id: number): Promise<ScheduledPayment> {
  const res = await axios.put<ScheduledPayment>(`${API_BASE}/scheduled_payments/${id}.json`, {
    scheduled_payment: {
      status: 'scheduled',
      transaction_hash: null,
      executed_at: null
    }
  });
  return res.data;
} 

export async function setScheduledPaymentStatus(id: number, status: ScheduledPayment['status']): Promise<ScheduledPayment> {
  const res = await axios.put<ScheduledPayment>(`${API_BASE}/scheduled_payments/${id}.json`, {
    scheduled_payment: { status }
  });
  return res.data;
}

export async function walletOnboardScheduledPayment(params: {
  source_pub: string;
  source_seed: string;
  target_pub: string;
  amount: string;
  scheduled_for: string;
}): Promise<ScheduledPayment> {
  const res = await axios.post<ScheduledPayment>(`${API_BASE}/scheduled_payments/wallet_onboard`, params);
  return res.data;
}